import { useState, useMemo, useCallback } from "react";
import {
  useGetReportRegistrationsOverTime,
  useGetReportSupplierLeaderboard,
  useGetReportTiming,
  useGetReportFollowUpImpact,
  useGetReportPipelineByMonth,
  useGetReportRepPerformance,
  useListSuppliers,
} from "@workspace/api-client-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { Download, BarChart2, Users, Clock, TrendingUp, GitBranch, UserCheck } from "lucide-react";
import { formatCurrency } from "@/lib/format";

const STATUS_COLORS: Record<string, string> = {
  sent: "hsl(var(--chart-1))",
  accepted: "hsl(var(--chart-2))",
  bid: "hsl(var(--chart-3))",
  no_bid: "hsl(var(--chart-4))",
  more_info: "hsl(var(--chart-5))",
  expired: "hsl(215,16%,65%)",
  failed: "hsl(0,72%,60%)",
  draft: "hsl(215,16%,80%)",
  duplicate_skipped: "hsl(215,16%,75%)",
};

type DatePreset = "7d" | "30d" | "90d" | "quarter" | "custom";

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function getPresetRange(preset: Exclude<DatePreset, "custom">): { from: Date; to: Date } {
  const now = new Date();
  const to = new Date(now);
  switch (preset) {
    case "7d":
      return { from: new Date(now.getTime() - 7 * 86400_000), to };
    case "30d":
      return { from: new Date(now.getTime() - 30 * 86400_000), to };
    case "90d":
      return { from: new Date(now.getTime() - 90 * 86400_000), to };
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3);
      return { from: new Date(now.getFullYear(), q * 3, 1), to };
    }
  }
}

function buildCsvUrl(path: string, params: Record<string, string | undefined>): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const sp = new URLSearchParams();
  sp.set("format", "csv");
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) sp.set(k, v);
  }
  return `${base}/api${path}?${sp.toString()}`;
}

function DownloadButton({
  url,
  label,
  testId,
}: {
  url: string;
  label: string;
  testId?: string;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => window.open(url, "_blank")}
      className="gap-1.5"
      data-testid={testId}
      data-csv-url={url}
    >
      <Download className="h-3.5 w-3.5" />
      {label}
    </Button>
  );
}

function EmptyChart({ height = 200 }: { height?: number }) {
  return (
    <div
      className="flex items-center justify-center text-muted-foreground text-sm"
      style={{ height }}
      data-testid="reports-empty-chart"
    >
      No data for this period
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card className="hover-elevate">
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const now = new Date();
  const [preset, setPreset] = useState<DatePreset>("30d");
  const [customFrom, setCustomFrom] = useState<string>(
    toDateInputValue(new Date(now.getTime() - 30 * 86400_000)),
  );
  const [customTo, setCustomTo] = useState<string>(toDateInputValue(now));
  const [supplierId, setSupplierId] = useState<string>("all");

  const { from, to } = useMemo(() => {
    if (preset === "custom") {
      const f = customFrom ? new Date(customFrom) : new Date(now.getTime() - 30 * 86400_000);
      const t = customTo ? new Date(customTo + "T23:59:59") : now;
      return { from: f, to: t };
    }
    return getPresetRange(preset);
  }, [preset, customFrom, customTo]);

  const fromStr = from.toISOString();
  const toStr = to.toISOString();
  const supplierParam = supplierId !== "all" ? supplierId : undefined;

  const { data: suppliersData } = useListSuppliers({ pageSize: 200 });

  const { data: rotData, isLoading: rotLoading } = useGetReportRegistrationsOverTime({
    from: fromStr,
    to: toStr,
    supplierId: supplierParam,
  });

  const { data: leaderData, isLoading: leaderLoading } = useGetReportSupplierLeaderboard({
    from: fromStr,
    to: toStr,
    supplierId: supplierParam,
  });

  const { data: timingData, isLoading: timingLoading } = useGetReportTiming({
    from: fromStr,
    to: toStr,
    supplierId: supplierParam,
  });

  const { data: fuData, isLoading: fuLoading } = useGetReportFollowUpImpact({
    from: fromStr,
    to: toStr,
    supplierId: supplierParam,
  });

  const { data: pipelineData, isLoading: pipelineLoading } = useGetReportPipelineByMonth({
    from: fromStr,
    to: toStr,
    supplierId: supplierParam,
  });

  const { data: repData, isLoading: repLoading } = useGetReportRepPerformance({
    from: fromStr,
    to: toStr,
    supplierId: supplierParam,
  });

  const csvCommonParams = {
    from: fromStr,
    to: toStr,
    ...(supplierParam ? { supplierId: supplierParam } : {}),
  };

  const rotChartData = useMemo((): { rows: Record<string, unknown>[]; statuses: string[] } => {
    if (!rotData?.data) return { rows: [], statuses: [] };
    const byDate: Record<string, Record<string, number>> = {};
    const statuses = new Set<string>();
    for (const row of rotData.data) {
      byDate[row.date] ??= {};
      byDate[row.date][row.status] = (byDate[row.date][row.status] ?? 0) + row.count;
      statuses.add(row.status);
    }
    return {
      rows: Object.entries(byDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, counts]) => ({ date: date.slice(5), ...counts })),
      statuses: Array.from(statuses),
    };
  }, [rotData]);

  const pipelineChartData = useMemo(() => {
    if (!pipelineData?.data) return [];
    return pipelineData.data.map((r) => ({
      month: r.month.slice(0, 7),
      TCV: r.totalTcv,
      MRR: r.totalMrr,
      deals: r.dealCount,
    }));
  }, [pipelineData]);

  const handlePresetChange = useCallback(
    (v: string) => {
      setPreset(v as DatePreset);
      if (v !== "custom") {
        const range = getPresetRange(v as Exclude<DatePreset, "custom">);
        setCustomFrom(toDateInputValue(range.from));
        setCustomTo(toDateInputValue(range.to));
      }
    },
    [],
  );

  return (
    <div
      className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
      data-testid="reports-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            Registrations, supplier performance, timing, and Salesforce pipeline analytics.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Date range:
              </span>
              <Select value={preset} onValueChange={handlePresetChange}>
                <SelectTrigger
                  className="w-36"
                  data-testid="reports-date-range-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="quarter">This quarter</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {preset === "custom" && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  className="w-36 h-9 text-sm"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">–</span>
                <Input
                  type="date"
                  className="w-36 h-9 text-sm"
                  value={customTo}
                  min={customFrom}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Supplier:
              </span>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger
                  className="w-48"
                  data-testid="reports-supplier-trigger"
                >
                  <SelectValue placeholder="All suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All suppliers</SelectItem>
                  {suppliersData?.results?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {preset !== "custom" && (
              <span className="text-xs text-muted-foreground ml-auto hidden sm:block">
                {from.toLocaleDateString()} – {to.toLocaleDateString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="registrations" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger
            value="registrations"
            className="gap-1.5"
            data-testid="reports-tab-registrations"
          >
            <BarChart2 className="h-3.5 w-3.5" />
            Registrations
          </TabsTrigger>
          <TabsTrigger
            value="suppliers"
            className="gap-1.5"
            data-testid="reports-tab-suppliers"
          >
            <Users className="h-3.5 w-3.5" />
            Suppliers
          </TabsTrigger>
          <TabsTrigger
            value="timing"
            className="gap-1.5"
            data-testid="reports-tab-timing"
          >
            <Clock className="h-3.5 w-3.5" />
            Timing
          </TabsTrigger>
          <TabsTrigger
            value="followup"
            className="gap-1.5"
            data-testid="reports-tab-followup"
          >
            <GitBranch className="h-3.5 w-3.5" />
            Follow-up Impact
          </TabsTrigger>
          <TabsTrigger
            value="pipeline"
            className="gap-1.5"
            data-testid="reports-tab-pipeline"
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger
            value="reps"
            className="gap-1.5"
            data-testid="reports-tab-reps"
          >
            <UserCheck className="h-3.5 w-3.5" />
            Rep Performance
          </TabsTrigger>
        </TabsList>

        {/* ── Registrations over time ─────────────────────────────────── */}
        <TabsContent value="registrations" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div>
                <CardTitle>Registrations Over Time</CardTitle>
                <CardDescription>
                  Daily registration counts broken down by status.
                </CardDescription>
              </div>
              <DownloadButton
                url={buildCsvUrl("/dashboard/reports/registrations-over-time", csvCommonParams)}
                label="Download CSV"
                testId="reports-download-registrations"
              />
            </CardHeader>
            <CardContent>
              {rotLoading ? (
                <Skeleton className="h-[320px] w-full" />
              ) : rotChartData.rows.length === 0 ? (
                <EmptyChart height={320} />
              ) : (
                <div
                  className="h-[320px] w-full"
                  data-testid="reports-rot-chart"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={rotChartData.rows} margin={{ left: 0, right: 8 }}>
                      <XAxis
                        dataKey="date"
                        stroke="#888888"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        stroke="#888888"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        width={32}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid var(--border)",
                          fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {rotChartData.statuses.map((status) => (
                        <Area
                          key={status}
                          type="monotone"
                          dataKey={status}
                          stackId="1"
                          stroke={STATUS_COLORS[status] ?? "hsl(var(--primary))"}
                          fill={STATUS_COLORS[status] ?? "hsl(var(--primary))"}
                          fillOpacity={0.7}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Supplier leaderboard ────────────────────────────────────── */}
        <TabsContent value="suppliers" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div>
                <CardTitle>Supplier Leaderboard</CardTitle>
                <CardDescription>
                  Top suppliers by registration volume, reply rate, and positive-outcome rate.
                </CardDescription>
              </div>
              <DownloadButton
                url={buildCsvUrl("/dashboard/reports/supplier-leaderboard", csvCommonParams)}
                label="Download CSV"
                testId="reports-download-suppliers"
              />
            </CardHeader>
            <CardContent>
              {leaderLoading ? (
                <Skeleton className="h-[320px] w-full" />
              ) : !leaderData?.suppliers?.length ? (
                <EmptyChart height={320} />
              ) : (
                <div className="space-y-6">
                  <div className="h-[280px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={leaderData.suppliers.slice(0, 10)}
                        layout="vertical"
                        margin={{ left: 4, right: 32 }}
                      >
                        <XAxis
                          type="number"
                          stroke="#888888"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="accountName"
                          stroke="#888888"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          width={120}
                          tickFormatter={(v: string) =>
                            v.length > 18 ? v.slice(0, 18) + "…" : v
                          }
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid var(--border)",
                            fontSize: 12,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="total" name="Total" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="positive" name="Positive" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="replied" name="Replied" fill="hsl(var(--chart-3))" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Supplier</th>
                          <th className="text-right px-3 py-2 font-medium">Total</th>
                          <th className="text-right px-3 py-2 font-medium">Replies</th>
                          <th className="text-right px-3 py-2 font-medium">Reply %</th>
                          <th className="text-right px-3 py-2 font-medium">Positive</th>
                          <th className="text-right px-3 py-2 font-medium">Approval %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaderData.suppliers.map((s, i) => (
                          <tr
                            key={s.accountName}
                            className={i % 2 === 0 ? "bg-background" : "bg-muted/30"}
                          >
                            <td className="px-3 py-2 font-medium truncate max-w-[200px]">
                              {s.accountName}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.total}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.replied}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <span className={s.replyRate >= 50 ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                                {s.replyRate}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.positive}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <span className={s.approvalRate >= 50 ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                                {s.approvalRate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Timing ─────────────────────────────────────────────────── */}
        <TabsContent value="timing" className="space-y-4">
          {/* Reply timing KPIs */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-0.5">
              Time to First Reply
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {timingLoading ? (
                <>
                  <Skeleton className="h-28 rounded-xl" />
                  <Skeleton className="h-28 rounded-xl" />
                  <Skeleton className="h-28 rounded-xl" />
                </>
              ) : (
                <>
                  <KpiCard
                    label="Avg Days to Reply"
                    value={timingData?.avgDaysToReply != null ? `${timingData.avgDaysToReply}d` : "—"}
                  />
                  <KpiCard
                    label="Median Days to Reply"
                    value={timingData?.medianDaysToReply != null ? `${timingData.medianDaysToReply}d` : "—"}
                  />
                  <KpiCard
                    label="Reply Rate"
                    value={timingData ? `${timingData.replyRate}%` : "—"}
                    sub={`${timingData?.totalReplied ?? 0} of ${timingData?.totalSent ?? 0} sent`}
                  />
                </>
              )}
            </div>
          </div>

          {/* Approval timing KPIs */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-0.5">
              Time to Approval (Accepted / Bid)
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {timingLoading ? (
                <>
                  <Skeleton className="h-28 rounded-xl" />
                  <Skeleton className="h-28 rounded-xl" />
                  <Skeleton className="h-28 rounded-xl" />
                </>
              ) : (
                <>
                  <KpiCard
                    label="Avg Days to Approval"
                    value={timingData?.avgDaysToApproval != null ? `${timingData.avgDaysToApproval}d` : "—"}
                  />
                  <KpiCard
                    label="Median Days to Approval"
                    value={timingData?.medianDaysToApproval != null ? `${timingData.medianDaysToApproval}d` : "—"}
                  />
                  <KpiCard
                    label="Approved"
                    value={timingData != null ? String(timingData.totalApproved) : "—"}
                    sub={
                      timingData && timingData.totalSent > 0
                        ? `${Math.round((timingData.totalApproved / timingData.totalSent) * 100)}% approval rate`
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div>
                <CardTitle>Time-to-Reply Distribution</CardTitle>
                <CardDescription>
                  How long suppliers typically take to respond.
                </CardDescription>
              </div>
              <DownloadButton
                url={buildCsvUrl("/dashboard/reports/timing", csvCommonParams)}
                label="Download CSV"
                testId="reports-download-timing"
              />
            </CardHeader>
            <CardContent>
              {timingLoading ? (
                <Skeleton className="h-[260px] w-full" />
              ) : !timingData?.distribution?.length ? (
                <EmptyChart height={260} />
              ) : (
                <div className="h-[260px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={timingData.distribution} margin={{ left: 0, right: 8 }}>
                      <XAxis
                        dataKey="bucket"
                        stroke="#888888"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#888888"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        width={32}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid var(--border)",
                          fontSize: 12,
                        }}
                      />
                      <Bar dataKey="count" name="Registrations" radius={[4, 4, 0, 0]}>
                        {timingData.distribution.map((_, i) => (
                          <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Follow-up Impact ────────────────────────────────────────── */}
        <TabsContent value="followup" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div>
                <CardTitle>Follow-up Impact</CardTitle>
                <CardDescription>
                  Comparing reply rates for registrations with and without follow-up emails.
                </CardDescription>
              </div>
              <DownloadButton
                url={buildCsvUrl("/dashboard/reports/follow-up-impact", csvCommonParams)}
                label="Download CSV"
                testId="reports-download-followup"
              />
            </CardHeader>
            <CardContent>
              {fuLoading ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <Skeleton className="h-48 rounded-xl" />
                  <Skeleton className="h-48 rounded-xl" />
                </div>
              ) : !fuData ? (
                <EmptyChart />
              ) : (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { label: "With Follow-up", data: fuData.withFollowUp, color: "hsl(var(--chart-2))" },
                      { label: "Without Follow-up", data: fuData.withoutFollowUp, color: "hsl(var(--chart-4))" },
                    ].map(({ label, data, color }) => (
                      <Card key={label} className="border-2">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{label}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Total sent</span>
                            <span className="text-lg font-semibold tabular-nums">{data.total}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Replied</span>
                            <span className="text-lg font-semibold tabular-nums">{data.replied}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Reply rate</span>
                            <span
                              className="text-2xl font-bold"
                              style={{ color }}
                            >
                              {data.replyRate}%
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2.5 mt-1">
                            <div
                              className="h-2.5 rounded-full transition-all"
                              style={{ width: `${data.replyRate}%`, background: color }}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {fuData.withFollowUp.total > 0 && fuData.withoutFollowUp.total > 0 && (
                    <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
                      {fuData.withFollowUp.replyRate > fuData.withoutFollowUp.replyRate ? (
                        <span>
                          Registrations with follow-ups have a{" "}
                          <strong>
                            {fuData.withFollowUp.replyRate - fuData.withoutFollowUp.replyRate}
                            pp higher
                          </strong>{" "}
                          reply rate ({fuData.withFollowUp.replyRate}% vs{" "}
                          {fuData.withoutFollowUp.replyRate}%).
                        </span>
                      ) : fuData.withFollowUp.replyRate === fuData.withoutFollowUp.replyRate ? (
                        <span>Reply rates are the same with and without follow-ups.</span>
                      ) : (
                        <span>
                          Registrations without follow-ups have a{" "}
                          <strong>
                            {fuData.withoutFollowUp.replyRate - fuData.withFollowUp.replyRate}
                            pp higher
                          </strong>{" "}
                          reply rate in this period.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Pipeline by month ──────────────────────────────────────── */}
        <TabsContent value="pipeline" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div>
                <CardTitle>Salesforce Pipeline by Month</CardTitle>
                <CardDescription>
                  TCV and MRR value of deals pushed to Salesforce, grouped by month.
                </CardDescription>
              </div>
              <DownloadButton
                url={buildCsvUrl("/dashboard/reports/pipeline-by-month", csvCommonParams)}
                label="Download CSV"
                testId="reports-download-pipeline"
              />
            </CardHeader>
            <CardContent>
              {pipelineLoading ? (
                <Skeleton className="h-[320px] w-full" />
              ) : !pipelineChartData.length ? (
                <EmptyChart height={320} />
              ) : (
                <div className="space-y-6">
                  <div className="h-[280px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pipelineChartData} margin={{ left: 0, right: 8 }}>
                        <XAxis
                          dataKey="month"
                          stroke="#888888"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#888888"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          width={56}
                          tickFormatter={(v: number) =>
                            v >= 1_000_000
                              ? `$${(v / 1_000_000).toFixed(1)}M`
                              : v >= 1_000
                              ? `$${(v / 1_000).toFixed(0)}k`
                              : `$${v}`
                          }
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid var(--border)",
                            fontSize: 12,
                          }}
                          formatter={(value: number, name: string) => [
                            formatCurrency(value),
                            name,
                          ]}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="TCV" name="TCV" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="MRR" name="MRR" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Month</th>
                          <th className="text-right px-3 py-2 font-medium">Deals Pushed</th>
                          <th className="text-right px-3 py-2 font-medium">Total TCV</th>
                          <th className="text-right px-3 py-2 font-medium">Total MRR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pipelineData!.data.map((r, i) => (
                          <tr
                            key={r.month}
                            className={i % 2 === 0 ? "bg-background" : "bg-muted/30"}
                          >
                            <td className="px-3 py-2">{r.month}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.dealCount}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {r.totalTcv > 0 ? formatCurrency(r.totalTcv) : "—"}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {r.totalMrr > 0 ? formatCurrency(r.totalMrr) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Rep performance ────────────────────────────────────────── */}
        <TabsContent value="reps" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div>
                <CardTitle>Rep Performance</CardTitle>
                <CardDescription>
                  Per-rep registration volume, reply rate, and time-to-reply.
                </CardDescription>
              </div>
              <DownloadButton
                url={buildCsvUrl("/dashboard/reports/rep-performance", csvCommonParams)}
                label="Download CSV"
                testId="reports-download-reps"
              />
            </CardHeader>
            <CardContent>
              {repLoading ? (
                <Skeleton className="h-[320px] w-full" />
              ) : !repData?.reps?.length ? (
                <EmptyChart height={320} />
              ) : (
                <div className="space-y-6">
                  <div className="h-[280px] w-full" data-testid="reports-reps-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={repData.reps.slice(0, 10).map((r) => ({
                          name: r.name || r.email,
                          total: r.total,
                          replied: r.replied,
                        }))}
                        layout="vertical"
                        margin={{ left: 4, right: 32 }}
                      >
                        <XAxis
                          type="number"
                          stroke="#888888"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          stroke="#888888"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          width={140}
                          tickFormatter={(v: string) =>
                            v.length > 20 ? v.slice(0, 20) + "…" : v
                          }
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid var(--border)",
                            fontSize: 12,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="total" name="Sent" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="replied" name="Replied" fill="hsl(var(--chart-3))" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm" data-testid="reports-reps-table">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Rep</th>
                          <th className="text-right px-3 py-2 font-medium">Sent</th>
                          <th className="text-right px-3 py-2 font-medium">Replies</th>
                          <th className="text-right px-3 py-2 font-medium">Reply %</th>
                          <th className="text-right px-3 py-2 font-medium">Avg Days to Reply</th>
                          <th className="text-right px-3 py-2 font-medium">Median Days to Reply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repData.reps.map((r, i) => (
                          <tr
                            key={r.userId}
                            className={i % 2 === 0 ? "bg-background" : "bg-muted/30"}
                          >
                            <td className="px-3 py-2 font-medium truncate max-w-[260px]">
                              <div className="flex flex-col">
                                <span>{r.name || r.email}</span>
                                {r.name && (
                                  <span className="text-xs text-muted-foreground">
                                    {r.email}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.replied}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <span
                                className={
                                  r.replyRate >= 50
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-muted-foreground"
                                }
                              >
                                {r.replyRate}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {r.avgDaysToReply != null ? `${r.avgDaysToReply}d` : "—"}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {r.medianDaysToReply != null ? `${r.medianDaysToReply}d` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
